import { Table, DatePicker, Space, Button, type TableColumnsType, type TableProps } from 'antd';
import { useState, useEffect, useMemo } from 'react';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { ExportExcelButton, ColumnManagerDropdown } from '../../common';
import { useColumnManager } from '../../../hooks';
import * as XLSX from 'xlsx';

const PaginatedTableBalanzaEgresos = ({ csvName, getTableData, getLoading }: any) => {

  // Fetch Data
  const [fullTableData, setFullTableData] = useState<any[]>([]);
  const [filteredTableData, setFilteredTableData] = useState<any[]>(fullTableData)
  const [loading, setLoading] = useState(false)

  const {user, getAccessTokenSilently} = useAuth0();

  const id_empresa = user?.id_empresa
  const id_cliente = user?.id_cliente
  const tipo_usuario = user?.tipo_usuario 

  const variables_api = `${tipo_usuario}/${id_empresa}/${id_cliente}`

  // Para filtrar
  const desired_output = (data: any, column: any) => {
      const unique_values = data
          .map((item: any) => item[column])
          .filter(
              (value: any, index: any, current_value: any) => current_value.indexOf(value) === index
          );
      return unique_values;
  };

  // Para cuando se filtre la tabla desde la tabla.
  // En lugar de modificar la tabla completa, usamos otra variable.
  const onChange: TableProps<DataType>['onChange'] = (pagination, filters, sorter, extra: any) => {
      setFilteredTableData(extra.currentDataSource);
    };

  //var inicio = getYearMonthDay(new Date(fechaInicio))
  //var fin = getYearMonthDay(new Date(fechaFin))

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const apiEndpoint = `${process.env.REACT_APP_REPORTES_URL}/despachos/${variables_api}?producto_id=7`;
        const token = await getAccessTokenSilently();
        const response = await fetch(apiEndpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        const respuesta = await response.json();
        setFullTableData(Array.isArray(respuesta) ? respuesta : []);
        getTableData(Array.isArray(respuesta) ? respuesta : []);
        getLoading(false);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
        getLoading(false);
        setLoading(false);
      }
    };
    fetchData();
  }, [getAccessTokenSilently]); 

  // Productos únicos para el filtro.
  const productoUnicos = desired_output(fullTableData, 'orden_producto')
    .map((producto: any) => ({text: producto, value: producto}));

  // Clientes únicos para el filtro (usando balanza_cliente como vendedor).
  const vendedorUnicos = desired_output(fullTableData, 'balanza_cliente')
    .map((vendedor: any) => ({text: vendedor, value: vendedor}));

  // Compradores únicos para el filtro (usando orden_cliente como comprador).
  const compradorUnicos = desired_output(fullTableData, 'orden_cliente')
    .map((comprador: any) => ({text: comprador, value: comprador}));

  // Transportistas únicos para el filtro.
  const transportistaUnicos = desired_output(fullTableData, 'balanza_transportista')
    .map((transportista: any) => ({text: transportista, value: transportista}));

  // Choferes únicos para el filtro.
  const choferUnicos = desired_output(fullTableData, 'balanza_chofer')
    .map((chofer: any) => ({text: chofer, value: chofer}));

  // Provincias de destino únicas para el filtro (usando despacho_observacion como provincia).
  const provinciaDestinoUnicos = desired_output(fullTableData, 'despacho_observacion')
    .map((provincia_destino: any) => ({text: provincia_destino, value: provincia_destino}));

  // Reusable date filter function
  const createDateFilter = (dateField: string) => {
    return {
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
        <div style={{ padding: 8 }}>
          <DatePicker.RangePicker
            value={selectedKeys[0]}
            onChange={(dates) => {
              setSelectedKeys(dates ? [dates] : []);
            }}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() => confirm()}
              size="small"
              style={{ width: 90 }}
            >
              Filter
            </Button>
            <Button
              onClick={() => {
                clearFilters?.();
                confirm();
              }}
              size="small"
              style={{ width: 90 }}
            >
              Reset
            </Button>
          </Space>
        </div>
      ),
      onFilter: (value: any, record: any) => {
        const date = new Date(record[dateField]);
        return date >= value[0].toDate() && date <= value[1].toDate();
      },
    };
  };

  // Table
  type DataType = {
    orden_producto : string
    vendedor : string
    comprador : string
    balanza_numero_remito : string
    balanza_dtv : string
    transportista : string
    balanza_chofer : string
    balanza_camion : string
    balanza_kilos_primera : string
    balanza_kilos_segunda : string
    balanza_peso : number
    despacho_fecha : string
    balanza_fecha_ticket : string
    despacho_observacion : string
    orden_nro_orden : number
    orden_tipo_display : string
    cantidad_fardos : number
    }
    
  const allColumns: TableColumnsType<DataType> = [
    {
      title: 'Producto',
      dataIndex: 'orden_producto',
      key: 'orden_producto',
      width: 120,
      ellipsis: true,

      filters: productoUnicos,
      onFilter: (value, record) => record.orden_producto == value
    },
    {
      title: 'Vendedor',
      dataIndex: 'vendedor',
      key: 'vendedor',
      width: 150,
      ellipsis: true,

      filters: vendedorUnicos,
      onFilter: (value, record) => record.vendedor == value
    },
    {
      title: 'Comprador',
      dataIndex: 'comprador',
      key: 'comprador',
      width: 150,
      ellipsis: true,

      filters: compradorUnicos,
      onFilter: (value, record) => record.comprador == value
    },
    {
      title: 'N° Remito',
      dataIndex: 'balanza_numero_remito',
      key: 'balanza_numero_remito',
      width: 110,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.balanza_numero_remito.localeCompare(b.balanza_numero_remito),
    },
    {
      title: 'DTV',
      dataIndex: 'balanza_dtv',
      key: 'balanza_dtv',
      width: 110,
      ellipsis: true,
    },
    {
      title: 'Transportista',
      dataIndex: 'transportista',
      key: 'transportista',
      width: 150,
      ellipsis: true,

      defaultSortOrder: 'descend',
      filters: transportistaUnicos,
      onFilter: (value, record) => record.transportista == value
    },
    {
      title: 'Chofer',
      dataIndex: 'balanza_chofer',
      key: 'balanza_chofer',
      width: 130,
      ellipsis: true,

      filters: choferUnicos,
      onFilter: (value, record) => record.balanza_chofer == value        
    },
    {
      title: 'Camión',
      dataIndex: 'balanza_camion',
      key: 'balanza_camion',
      width: 120,
      ellipsis: true,

    },
    {
      title: 'Kg. 1ra',
      dataIndex: 'balanza_kilos_primera',
      key: 'balanza_kilos_primera',
      width: 100,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => parseFloat(a.balanza_kilos_primera) - parseFloat(b.balanza_kilos_primera),
    },
    {
      title: 'Kg. 2da',
      dataIndex: 'balanza_kilos_segunda',
      key: 'balanza_kilos_segunda',
      width: 100,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => parseFloat(a.balanza_kilos_segunda) - parseFloat(b.balanza_kilos_segunda),
    },
    {
      title: 'Peso Neto',
      dataIndex: 'balanza_peso',
      key: 'balanza_peso',
      width: 110,
      align: 'right',

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.balanza_peso - b.balanza_peso,
    },
    {
      title: 'Fecha Desp.',
      dataIndex: 'despacho_fecha',
      key: 'despacho_fecha',
      width: 120,

      ...createDateFilter('despacho_fecha')
    },
    {
      title: 'Fecha Balanza',
      dataIndex: 'balanza_fecha_ticket',
      key: 'balanza_fecha_ticket',
      width: 130,

      ...createDateFilter('balanza_fecha_ticket')
    },
    {
      title: 'Observ.',
      dataIndex: 'despacho_observacion',
      key: 'despacho_observacion',
      width: 120,
      ellipsis: true,

    },
    {
      title: 'N° Orden',
      dataIndex: 'orden_nro_orden',
      key: 'orden_nro_orden',
      width: 100,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.orden_nro_orden - b.orden_nro_orden,
    },
    {
      title: 'Tipo Orden',
      dataIndex: 'orden_tipo_display',
      key: 'orden_tipo_display',
      width: 110,
      ellipsis: true,

    },
    {
      title: 'Cant. Fardos',
      dataIndex: 'cantidad_fardos',
      key: 'cantidad_fardos',
      width: 110,

      defaultSortOrder: 'descend',
      sorter: (a, b) => a.cantidad_fardos - b.cantidad_fardos,
    },
  ]

  // Lista de todas las columnas disponibles (keys)
  const allColumnKeys = allColumns.map(col => col.key as string).filter(Boolean);

  // Nombres amigables para las columnas
  const columnNames: Record<string, string> = {
      'orden_producto': 'Producto',
      'vendedor': 'Vendedor',
      'comprador': 'Comprador',
      'balanza_numero_remito': 'N° Remito',
      'balanza_dtv': 'DTV',
      'transportista': 'Transportista',
      'balanza_chofer': 'Chofer',
      'balanza_camion': 'Camión',
      'balanza_kilos_primera': 'Kg. 1ra',
      'balanza_kilos_segunda': 'Kg. 2da',
      'balanza_peso': 'Peso Neto',
      'despacho_fecha': 'Fecha Desp.',
      'balanza_fecha_ticket': 'Fecha Balanza',
      'despacho_observacion': 'Observ.',
      'orden_nro_orden': 'N° Orden',
      'orden_tipo_display': 'Tipo Orden',
      'cantidad_fardos': 'Cant. Fardos'
  };

  // Visibilidad + orden de columnas (persistido) — reemplaza el manejo manual de antes.
  const columnManager = useColumnManager({
      storageKey: 'balanza_egresos',
      allKeys: allColumnKeys,
  });

  // Columnas visibles, en el orden elegido por el usuario en el selector de columnas
  const filteredColumns = useMemo(() => {
      const columnsByKey = new Map(allColumns.map(col => [col.key as string, col]));
      return columnManager.orderedVisibleKeys
          .map(key => columnsByKey.get(key))
          .filter((col): col is (typeof allColumns)[number] => Boolean(col));
  }, [columnManager.orderedVisibleKeys, allColumns]);

  const columns: TableColumnsType<DataType> = filteredColumns;

    const data: DataType[] = fullTableData.map((fila: any) => {
        return {
          orden_producto : fila.orden_producto,
          vendedor : fila.balanza_cliente,
          comprador : fila.orden_cliente,
          balanza_numero_remito : fila.balanza_numero_remito,
          balanza_dtv : fila.balanza_dtv || '',
          transportista : fila.balanza_transportista,
          balanza_chofer : fila.balanza_chofer,
          balanza_camion : fila.balanza_camion,
          balanza_kilos_primera : fila.balanza_kilos_primera,
          balanza_kilos_segunda : fila.balanza_kilos_segunda,
          balanza_peso : fila.balanza_peso,
          despacho_fecha : fila.despacho_fecha,
          balanza_fecha_ticket : fila.balanza_fecha_ticket,
          despacho_observacion : fila.despacho_observacion,
          orden_nro_orden : fila.orden_nro_orden,
          orden_tipo_display : fila.orden_tipo_display,
          cantidad_fardos : fila.cantidad_fardos,
        }
    });

  // Función para exportar a Excel
  const handleExportExcel = () => {
      try {
          const sourceData: any[] = filteredTableData.length > 0 ? filteredTableData : data;
          const exportData = sourceData.map((row: any) => {
              const exportRow: Record<string, any> = {};
              columnManager.orderedVisibleKeys.forEach(key => {
                  exportRow[columnNames[key] || key] = row[key];
              });
              return exportRow;
          });
          const worksheet = XLSX.utils.json_to_sheet(exportData);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, 'Balanza Egresos');
          XLSX.writeFile(workbook, csvName || `balanza_egresos_${new Date().toISOString().split('T')[0]}.xlsx`);
      } catch (error) {
          console.error('Error al exportar Excel:', error);
      }
  };

    return (
      <div>
        <div style={{ 
            marginBottom: '16px', 
            display: 'flex', 
            gap: '12px', 
            justifyContent: 'flex-end',
            alignItems: 'center',
            flexWrap: 'wrap'
        }}>
            <ColumnManagerDropdown
                manager={columnManager}
                labels={columnNames}
                buttonSize="middle"
            />
            <ExportExcelButton
                onClick={handleExportExcel}
                size="middle"
                disabled={fullTableData.length === 0}
            />
        </div>
        <Table 
            columns={columns} 
            dataSource={data}
            bordered
            size="middle"
            scroll={{ x: 'max-content', y: 'max(150px, calc(100vh - 400px))' }}
            onChange={onChange}
            loading={loading}
            pagination={{
                pageSize: 50,
                showSizeChanger: true,
                showTotal: (total, range) => `${range[0]}-${range[1]} de ${total}`,
                pageSizeOptions: ['10', '25', '50', '100'],
                position: ['bottomRight']
            }}
        >
        </Table>
      </div>
    );
  };
  
  export default withAuthenticationRequired(PaginatedTableBalanzaEgresos);
